import { match } from 'variant';
import type { Headers } from '@wix/cli-http-client';
import { client } from '../client.js';
import type { ApiKeyAuth } from '../schemas.js';
import {
  readApiKeyAuthData,
  saveApiKeyAuthData,
  deleteAuthData,
} from '../auth-data.js';
import type {
  ApiKeyAuthStrategy,
  AuthStrategyState,
} from './auth-strategy-types.js';

function createApiKeyAuthState({
  authData: { token, accountId, userInfo },
  siteId,
}: {
  authData: ApiKeyAuth;
  siteId?: string;
}): AuthStrategyState {
  return {
    getAccessToken: async (_options?) => token,
    getRefreshToken: () => {
      throw new Error('API key auth does not support refresh tokens');
    },
    getRequestHeaders: (options): Headers => {
      const siteIdToUse = siteId ?? options?.siteId;

      if (siteIdToUse) {
        return { 'wix-site-id': siteIdToUse };
      }

      return { 'wix-account-id': accountId };
    },
    userInfo,
  };
}

export const apiKeyAuthStrategy: ApiKeyAuthStrategy = {
  async isLoggedIn({ siteId }: { siteId?: string } = {}) {
    const apiKeyAuth = await readApiKeyAuthData();

    return match(apiKeyAuth, {
      Authenticated: ({ payload }) => {
        return createApiKeyAuthState({ authData: payload, siteId });
      },
      Unauthenticated: () => null,
    });
  },
  async login({ token }) {
    const { accountId, accountOwner, slug } = await client.getMyAccount({
      token,
    });

    const authData: ApiKeyAuth = {
      token,
      accountId,
      userInfo: {
        userId: accountOwner,
        // TODO: this should be the email of the account owner, not the slug
        // https://jira.wixpress.com/browse/FEDINF-12201
        email: slug,
      },
    };

    await deleteAuthData();
    await saveApiKeyAuthData(authData);

    return createApiKeyAuthState({ authData });
  },
};
